import React, { useEffect } from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  toast,
} from '@workwhile/ui'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  createBillRateFlowSchema,
  CreateBillRateFlowSchema,
} from '../schemas/createBillRate'
import { useCreateBillRateFlowContextValue } from '../context/CreateBillRateFlowProvider'
import { useBillRatesContextValue } from '../../../context/BillRatesProvider'
import { useBillRatesWorkflowsContextValue } from '../../../context/BillRatesWorkflowsProvider'
import { CompanyPicker } from '../../../components/CompanyPicker'
import { PositionPicker } from '../../../components/PositionPicker'

export const CreateBillRateFlowMain = () => {
  const {
    createBillRateFlow: { isOpen, close },
  } = useBillRatesWorkflowsContextValue()

  const {
    state: { companyId: filteredCompanyId },
  } = useBillRatesContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useCreateBillRateFlowContextValue()

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    resetField,
    watch,
    control,
  } = useForm<CreateBillRateFlowSchema>({
    resolver: zodResolver(createBillRateFlowSchema),
    defaultValues: {
      companyId: undefined,
      positionId: undefined,
      payRate: undefined,
      billRate: undefined,
    },
  })

  const companyId = watch('companyId')

  useEffect(() => {
    if (!isOpen) return

    reset({
      companyId: filteredCompanyId ?? undefined,
      positionId: undefined,
      payRate: undefined,
      billRate: undefined,
    })
  }, [isOpen, filteredCompanyId, reset])

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(data, {
      onSuccess: () => {
        toast.success('Bill rate created successfully', {
          position: 'top-center',
        })
        handleClose()
      },
    })
  })

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Create
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Create bill rate
        </Heading>

        <Box mt={4}>
          <Controller
            control={control}
            name="companyId"
            render={({ field, fieldState }) => (
              <CompanyPicker
                value={field.value}
                onChange={(value) => {
                  field.onChange(value)
                  resetField('positionId')
                }}
                error={fieldState.error?.message?.toString()}
              />
            )}
          />
        </Box>

        <Box mt={4}>
          <Controller
            control={control}
            name="positionId"
            render={({ field, fieldState }) => (
              <PositionPicker
                value={field.value}
                onChange={field.onChange}
                companyId={companyId}
                error={fieldState.error?.message?.toString()}
              />
            )}
          />
        </Box>

        <Field
          label="Pay Rate"
          error={errors.payRate?.message?.toString()}
          // Intentionally disabled. This should work, unsure why it's throwing an error
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          mt={4}
        >
          <Input
            type="number"
            step="0.01"
            min="0.01"
            max="10000"
            aria-label="Pay rate"
            placeholder="e.g. 24.58"
            {...register('payRate', {
              setValueAs: (v) =>
                v === '' || v == null ? undefined : Number(v),
            })}
          />
        </Field>

        <Field
          label="Charge Rate"
          error={errors.billRate?.message?.toString()}
          // Intentionally disabled. This should work, unsure why it's throwing an error
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          mt={4}
        >
          <Input
            type="number"
            step="0.01"
            min="0.01"
            max="10000"
            aria-label="Charge rate"
            placeholder="e.g. 32.00"
            {...register('billRate', {
              setValueAs: (v) =>
                v === '' || v == null ? undefined : Number(v),
            })}
          />
        </Field>

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
